import { nanoid } from 'nanoid';
import { ICreateNodePayload } from '@/editor/editor-block/store/types/actions';
import { ICellPayload, IRowPayload } from '@/editor/types/models-update';
import { ICell, IEditorBlock, INode, IRow, ISection, IExtractNodeIds } from '@/editor/types';
import { CellType, ENodeType, ERowType } from '@/editor/types/enums';
import { IEditorContentState } from '@/editor/editor-block/store/editor-content.types';
import { EditorHelper } from '@/editor/helpers/editor.helper';
import { fallbackTextContent } from '@/editor/editor-block/constants';

export class ContentActionHelper {
  static getBlockById({ blocks }: IEditorContentState, id: string): IEditorBlock {
    return EditorHelper.deepCopy(blocks.find(({ contentId }: IEditorBlock) => contentId === id));
  }

  static getRowPayload(type: ERowType, payload?: ICreateNodePayload): IRowPayload {
    if (type === ERowType.SideBySide) {
      return {
        rowId: nanoid(),
        rowType: ERowType.SideBySide,
        cells: [
          {
            cellId: nanoid(),
            width: 50,
            nodes: [{ nodeId: nanoid(), nodeType: ENodeType.Image }],
          },
          {
            cellId: nanoid(),
            width: 50,
            nodes: [{ nodeId: nanoid(), nodeType: ENodeType.Text, content: fallbackTextContent }],
          },
        ],
      };
    } else if (type === ERowType.Columns) {
      return {
        columnCount: 3,
        rowId: nanoid(),
        rowType: ERowType.Columns,
        cells: Array.from({ length: 3 }).map(() => ({
          cellId: nanoid(),
          nodes: [
            { nodeId: nanoid(), nodeType: ENodeType.Image },
            { nodeId: nanoid(), nodeType: ENodeType.Text, content: fallbackTextContent },
          ],
          cellType: CellType.ImageAndText,
        })),
      };
    } else {
      if (!payload) {
        return {
          rowId: nanoid(),
          rowType: ERowType.Standard,
          cells: [{ cellId: nanoid(), width: 100, nodes: [] }],
        };
      }

      const { nodeType, ...rest }: ICreateNodePayload = payload;
      const nodes = [{ nodeId: nanoid(), nodeType, ...rest }];

      return {
        rowId: nanoid(),
        rowType: ERowType.Standard,
        cells: [{ cellId: nanoid(), width: 100, nodes }],
      };
    }
  }

  static getNodeIdsToMove(
    { blocks }: IEditorContentState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      append = true,
    }: IExtractNodeIds
  ): string[] {
    const cell: ICell = blocks
      .find(({ contentId: id }: IEditorBlock) => id === contentId)
      ?.content.sections.find(({ id }: ISection) => id === targetSectionId)
      ?.rows.find(({ id }: IRow) => id === targetRowId)
      ?.cells.find(({ id }: ICell) => id === targetCellId);

    if (!cell) {
      return [];
    }

    const index = cell.nodes.findIndex(({ id }: INode) => id === targetNodeId);

    if (append) {
      return cell.nodes.slice(index + 1).map(({ id }: INode) => id);
    }

    return cell.nodes.slice(index).map(({ id }: INode) => id);
  }

  static cellToCellPayload = ({ id, type, width, nodes }: ICell): ICellPayload => {
    const newNodes = nodes.map(node => {
      const { id: nodeId, type: nodeType, ...rest } = node;
      return { ...rest, nodeId, nodeType };
    });

    return { cellId: id, cellType: type, width, nodes: newNodes };
  };
}
